import { useId, useState } from 'react';
import { useI18n } from '@/hooks/useI18n';

interface HashtagEditorProps {
  id?: string;
  describedBy?: string;
  tags: string[];
  onChange: (tags: string[]) => void;
  min?: number;
  max?: number;
}

export const validateHashtagCount = (tags: string[], min = 3, max = 5): 'min' | 'max' | null => {
  if (tags.length < min) return 'min';
  if (tags.length > max) return 'max';
  return null;
};

export const HashtagEditor = ({
  id,
  describedBy,
  tags,
  onChange,
  min = 3,
  max = 5,
}: HashtagEditorProps) => {
  const { t } = useI18n();
  const generatedId = useId();
  const controlId = id || generatedId;
  const countId = `${controlId}-count`;
  const [input, setInput] = useState('');

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag();
    } else if (e.key === 'Backspace' && !input && tags.length > 0) {
      onChange(tags.slice(0, -1));
    }
  };

  const addTag = () => {
    const val = input.trim().replace(/^#/, '');
    if (val && !tags.includes(val) && tags.length < max) {
      onChange([...tags, val]);
      setInput('');
    }
  };

  const removeTag = (tag: string) => {
    onChange(tags.filter((t) => t !== tag));
  };

  const countError = validateHashtagCount(tags, min, max);

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
        {tags.map((tag) => (
          <span
            key={tag}
            style={{
              display: 'inline-flex',
              maxWidth: '100%',
              overflowWrap: 'anywhere',
              alignItems: 'center',
              gap: 4,
              padding: '4px 8px',
              borderRadius: 6,
              background: 'var(--surface-panel-head)',
              color: 'var(--cyan)',
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            #{tag}
            <button
              type="button"
              aria-label={`Удалить хештег ${tag}`}
              onClick={() => removeTag(tag)}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'inherit',
                padding: 0,
                width: 24,
                height: 24,
                flexShrink: 0,
                cursor: 'pointer',
                opacity: 0.7,
              }}
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <div className="rg-hashtag-entry">
        <input
          id={controlId}
          aria-label={id ? undefined : t('publisher.field.hashtags')}
          aria-describedby={[describedBy, countId].filter(Boolean).join(' ')}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={addTag}
          disabled={tags.length >= max}
          placeholder={
            tags.length >= max
              ? t('publisher.validation.tags.max')
              : t('publisher.field.hashtags.hint')
          }
          className="form-input"
          style={{
            width: '100%',
            padding: '8px 12px',
            borderRadius: 8,
            border: '1px solid var(--border-hard)',
            background: 'var(--surface-card-a)',
            color: 'var(--text)',
          }}
        />
        <button
          type="button"
          className="rg-btn"
          disabled={tags.length >= max || !input.trim().replace(/^#/, '')}
          onClick={addTag}
        >
          Добавить
        </button>
      </div>
      <p
        id={countId}
        aria-live="polite"
        style={{
          margin: '6px 0 0',
          fontSize: 12,
          color: countError === 'min' ? '#ff7b65' : 'var(--text-muted)',
        }}
      >
        {countError === 'min' ? `Добавьте минимум ${min} хештега. ` : ''}Добавлено: {tags.length} из{' '}
        {max}.
      </p>
    </div>
  );
};
